import { AnyAction, combineReducers } from 'redux';
import { ReceiptAlert } from '@synergy/models';

import {
    INVOICE_DETAIL_DELETE_ALERT,
    INVOICE_DETAIL_RESET,
    INVOICE_DETAIL_SAVE_REFUSED,
    INVOICE_DETAIL_UPDATE_ALERT,
} from './invoice-detail-actions';
import {
    INVOICE_DETAIL_OVERVIEW_RECEIVE,
    INVOICE_DETAIL_OVERVIEW_SAVE,
    INVOICE_DETAIL_OVERVIEW_SAVE_ERROR,
} from './overview/invoice-detail-overview-actions';
import {
    INVOICE_DETAIL_POSITIONS_RECEIVE,
    INVOICE_DETAIL_POSITIONS_SAVE,
    INVOICE_DETAIL_POSITIONS_SAVE_ERROR,
} from './positions/invoice-detail-positions-actions';
import {
    InvoiceDetailOverviewReducerState,
    invoiceDetailOverviewReducer,
} from './overview/invoice-detail-overview-reducer';
import {
    InvoiceDetailPositionsReducerState,
    invoiceDetailPositionsReducer,
} from './positions/invoice-detail-positions-reducer';

interface InvoiceDetailCommonReducerState {
    receiptAlert?: ReceiptAlert;
    positionsRequesting: boolean;
    overviewRequesting: boolean;
    /**
     * Set once a save was refused, so the parts that are missing may say so.
     * Before the first attempt a new invoice is incomplete by nature and must
     * not be marked as faulty (#2206, R-134).
     */
    saveRefused?: boolean;
}

const invoiceDetailCommonInitialState: InvoiceDetailCommonReducerState = {
    positionsRequesting: false,
    overviewRequesting: false,
};

const invoiceDetailCommonReducer = (
    state: InvoiceDetailCommonReducerState = invoiceDetailCommonInitialState,
    action: AnyAction
) => {
    switch (action.type) {
        case INVOICE_DETAIL_SAVE_REFUSED: {
            return { ...state, saveRefused: true };
        }

        case INVOICE_DETAIL_OVERVIEW_RECEIVE: {
            const invoice = action['invoice'];
            return {
                ...state,
                receiptAlert: invoice.receiptAlert,
                overviewRequesting: false,
            };
        }

        case INVOICE_DETAIL_POSITIONS_RECEIVE: {
            return {
                ...state,
                positionsRequesting: false,
            };
        }

        case INVOICE_DETAIL_OVERVIEW_SAVE: {
            return {
                ...state,
                overviewRequesting: true,
            };
        }

        // Reset the spinner when a save fails, otherwise it stays visible forever
        case INVOICE_DETAIL_OVERVIEW_SAVE_ERROR: {
            return {
                ...state,
                overviewRequesting: false,
            };
        }

        case INVOICE_DETAIL_POSITIONS_SAVE: {
            return {
                ...state,
                positionsRequesting: true,
            };
        }

        case INVOICE_DETAIL_POSITIONS_SAVE_ERROR: {
            return {
                ...state,
                positionsRequesting: false,
            };
        }

        case INVOICE_DETAIL_UPDATE_ALERT: {
            return {
                ...state,
                receiptAlert: action['alert'],
            };
        }

        case INVOICE_DETAIL_DELETE_ALERT: {
            return {
                ...state,
                receiptAlert: undefined,
            };
        }

        case INVOICE_DETAIL_RESET: {
            return invoiceDetailCommonInitialState;
        }

        default:
            return state;
    }
};

export interface InvoiceDetailReducerState {
    overview: InvoiceDetailOverviewReducerState;
    positions: InvoiceDetailPositionsReducerState;
    common: InvoiceDetailCommonReducerState;
}

export const invoiceDetailReducer = combineReducers<InvoiceDetailReducerState>({
    overview: invoiceDetailOverviewReducer,
    positions: invoiceDetailPositionsReducer,
    common: invoiceDetailCommonReducer,
});
